import { useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  IconButton,
  Input,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { OriginsApi } from "../api/origins";
import LogoUpload from "../components/LogoUpload";
import logger from "../analytics/logger";

// Lowercase + strip anything outside [a-z0-9-] so the box self-normalizes as
// the user types — matches the server-side slug contract.
function normalizeSlug(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9-]/g, "");
}

export default function OriginNew() {
  const navigate = useNavigate();
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [welcomeHeadline, setWelcomeHeadline] = useState("");
  const [welcomeBody, setWelcomeBody] = useState("");
  const [buttonText, setButtonText] = useState("Get Started");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [mobileLogoUrl, setMobileLogoUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = () => navigate("/origins");

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await OriginsApi.create({
        slug: slug.trim(),
        display_name: displayName.trim() || null,
        welcome_headline: welcomeHeadline.trim() || null,
        welcome_body: welcomeBody.trim() || null,
        button_text: buttonText.trim() || "Get Started",
        logo_url: logoUrl,
        mobile_logo_url: mobileLogoUrl,
      });
      navigate(`/origins/${created.id}`);
    } catch (e: unknown) {
      logger.error("origin_create_failed", e);
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Failed to create origin";
      setError(msg === "slug_taken" ? "That slug is already taken." : msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={6}>
      <Flex align="center" justify="space-between" mb={4}>
        <HStack>
          <IconButton
            aria-label="Back"
            icon={<ArrowBackIcon />}
            variant="ghost"
            onClick={cancel}
          />
          <Heading size="lg">Add origin</Heading>
        </HStack>
        <HStack>
          <Button size="sm" variant="ghost" onClick={cancel} isDisabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={save}
            isLoading={saving}
            isDisabled={slug.trim().length < 3}
          >
            Save
          </Button>
        </HStack>
      </Flex>

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Stack spacing={4} maxW="lg">
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Slug
          </Text>
          <Input
            size="sm"
            value={slug}
            onChange={(e) => setSlug(normalizeSlug(e.target.value))}
            placeholder="acme-conference"
          />
          <Text fontSize="xs" color="gray.500" mt={1}>
            Used in URL https://www.humancreator.com/{slug || "{slug}"}
          </Text>
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Display name
          </Text>
          <Input
            size="sm"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Acme Conference"
          />
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Welcome headline
          </Text>
          <Input
            size="sm"
            value={welcomeHeadline}
            onChange={(e) => setWelcomeHeadline(e.target.value)}
            placeholder="Welcome to Acme!"
          />
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Welcome body
          </Text>
          <Textarea
            size="sm"
            value={welcomeBody}
            onChange={(e) => setWelcomeBody(e.target.value)}
            placeholder="Branding copy shown on the welcome page"
            rows={4}
          />
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Button text
          </Text>
          <Input
            size="sm"
            value={buttonText}
            onChange={(e) => setButtonText(e.target.value)}
            placeholder="Get Started"
          />
        </Box>
        <LogoUpload label="Logo URL" value={logoUrl} onChange={setLogoUrl} />
        <LogoUpload
          label="Mobile logo URL"
          helperText="optimal image size is 300 x 200"
          previewRatio={3 / 2}
          value={mobileLogoUrl}
          onChange={setMobileLogoUrl}
        />
      </Stack>
    </Box>
  );
}
